import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchResults, clearSearch, searchContent as searchContentAPI } from '../redux/slices/contentSlice';
import { searchContent } from '../data/mockData';
import styled from 'styled-components';
import ThumbnailCard from '../components/ThumbnailCard';
import { motion } from 'framer-motion';

const SearchContainer = styled.div`
  min-height: 100vh;
  background-color: #141414;
  padding: 2rem 4% 4rem;

  @media (max-width: 768px) {
    padding: 1.5rem 20px 3rem;
  }
`;

const SearchHeader = styled.div`
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 600px;
  background: rgba(20, 20, 20, 0.8);
  border: 2px solid #333;
  color: white;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 8px;
  margin-bottom: 1rem;

  &::placeholder {
    color: #808080;
  }

  &:focus {
    outline: none;
    border-color: #E50914;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1rem;
  }
`;

const ResultsInfo = styled.div`
  font-size: 1.1rem;
  color: #808080;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ResultsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.8rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 0.6rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #808080;
`;

const NoResultsTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const NoResultsText = styled.p`
  font-size: 1.1rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PopularSearches = styled.div`
  margin-top: 3rem;
`;

const PopularTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
`;

const PopularGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.8rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 0.6rem;
  }
`;

const Search = () => {
  const dispatch = useDispatch();
  const { searchQuery, searchResults, allContent, scrapedContent, apiHealthy } = useSelector(state => state.content);
  const [localQuery, setLocalQuery] = useState(searchQuery);

  useEffect(() => {
    if (searchQuery) {
      // Use API search if available, otherwise fallback to mock search
      if (apiHealthy) {
        dispatch(searchContentAPI(searchQuery));
      } else {
        const results = searchContent(searchQuery);
        dispatch(setSearchResults(results));
      }
    } else {
      dispatch(setSearchResults([]));
    }
  }, [searchQuery, dispatch, apiHealthy]);

  useEffect(() => {
    return () => {
      if (!searchQuery) {
        dispatch(clearSearch());
      }
    };
  }, [searchQuery, dispatch]);

  const handleSearchChange = (e) => {
    setLocalQuery(e.target.value);
    if (e.target.value.trim()) {
      // Use API search if available, otherwise fallback to mock search
      if (apiHealthy) {
        dispatch(searchContentAPI(e.target.value.trim()));
      } else {
        const results = searchContent(e.target.value.trim());
        dispatch(setSearchResults(results));
      }
    } else {
      dispatch(setSearchResults([]));
    }
  };

  // Use scraped content for popular if available
  const popularContent = (apiHealthy && scrapedContent.trending.length > 0) ?
    scrapedContent.trending.slice(0, 8) :
    allContent.slice(0, 8);

  return (
    <SearchContainer>
      <SearchHeader>
        <SearchInput
          type="text"
          placeholder="Search for movies, TV shows, actors..."
          value={localQuery}
          onChange={handleSearchChange}
          autoFocus
        />
      </SearchHeader>

      {searchResults.length > 0 ? (
        <>
          <ResultsInfo>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{localQuery}"
          </ResultsInfo>

          <ResultsGrid
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {searchResults.map((content, index) => (
              <ThumbnailCard
                key={content.id}
                content={content}
                index={index}
              />
            ))}
          </ResultsGrid>
        </>
      ) : localQuery ? (
        <NoResults>
          <NoResultsTitle>No results found</NoResultsTitle>
          <NoResultsText>
            Sorry, we couldn't find anything matching "{localQuery}".
            <br />
            Try searching for something else.
          </NoResultsText>
        </NoResults>
      ) : (
        <PopularSearches>
          <PopularTitle>Popular on Netflix</PopularTitle>
          <PopularGrid>
            {popularContent.map((content, index) => (
              <ThumbnailCard
                key={content.id}
                content={content}
                index={index}
              />
            ))}
          </PopularGrid>
        </PopularSearches>
      )}
    </SearchContainer>
  );
};

export default Search;